/**
 * CREATIVE DETONATOR - Application Event Types
 *
 * Centralized event name constants to prevent typos
 * and provide documentation of available events.
 *
 * Event naming convention: <domain>:<action>
 *
 * @version 1.0.0
 * @description Implements REFACTOR-03 (Event Bus Decoupling)
 */

// ============================================================================
// IDEA EVENTS
// ============================================================================

/**
 * Events related to idea lifecycle and updates
 */
export const IDEA_EVENTS = {
  /** Fired when a new idea is created */
  CREATED: 'idea:created',
  /** Fired when an idea is updated (any field change) */
  UPDATED: 'idea:updated',
  /** Fired when an idea is deleted */
  DELETED: 'idea:deleted',
  /** Fired when an idea is saved to favorites */
  SAVED: 'idea:saved',
  /** Fired when an idea is unsaved from favorites */
  UNSAVED: 'idea:unsaved',
  /** Fired when an idea is refined via Art Director */
  REFINED: 'idea:refined',
  /** Fired when an image is generated for an idea */
  IMAGE_GENERATED: 'idea:image-generated',
  /** Fired when a video is generated for an idea */
  VIDEO_GENERATED: 'idea:video-generated',
  /** Fired when an idea is selected in grid */
  SELECTED: 'idea:selected',
  /** Fired when an idea is deselected in grid */
  DESELECTED: 'idea:deselected',
  /** Fired when grid selection changes (includes all selected IDs) */
  SELECTION_CHANGED: 'idea:selection-changed'
};

// ============================================================================
// SESSION EVENTS
// ============================================================================

/**
 * Events related to generation sessions
 */
export const SESSION_EVENTS = {
  /** Fired when a new session is created */
  CREATED: 'session:created',
  /** Fired when a session is loaded */
  LOADED: 'session:loaded',
  /** Fired when session data is updated */
  UPDATED: 'session:updated',
  /** Fired when a session is deleted */
  DELETED: 'session:deleted',
  /** Fired when a generation session completes */
  COMPLETED: 'session:completed'
};

// ============================================================================
// GENERATOR EVENTS
// ============================================================================

/**
 * Events related to the idea generator workflow
 */
export const GENERATOR_EVENTS = {
  /** Fired when generator phase changes (1-4) */
  PHASE_CHANGED: 'generator:phase-changed',
  /** Fired when brief data is updated */
  BRIEF_UPDATED: 'generator:brief-updated',
  /** Fired when selected formats change */
  FORMATS_CHANGED: 'generator:formats-changed',
  /** Fired when selected triggers change */
  TRIGGERS_CHANGED: 'generator:triggers-changed',
  /** Fired when idea generation starts */
  GENERATION_STARTED: 'generator:generation-started',
  /** Fired during generation with progress updates */
  GENERATION_PROGRESS: 'generator:generation-progress',
  /** Fired when idea generation completes successfully */
  GENERATION_COMPLETED: 'generator:generation-completed',
  /** Fired when idea generation fails */
  GENERATION_ERROR: 'generator:generation-error',
  /** Fired when generator state is reset */
  RESET: 'generator:reset'
};

// ============================================================================
// IMAGE GENERATION EVENTS
// ============================================================================

/**
 * Events related to image generation
 */
export const IMAGE_EVENTS = {
  /** Fired when image generation starts */
  GENERATION_STARTED: 'image:generation-started',
  /** Fired when image generation completes */
  GENERATION_COMPLETED: 'image:generation-completed',
  /** Fired when image generation fails */
  GENERATION_FAILED: 'image:generation-failed',
  /** Fired when image editing starts */
  EDIT_STARTED: 'image:edit-started',
  /** Fired when image editing completes */
  EDIT_COMPLETED: 'image:edit-completed'
};

// ============================================================================
// VIDEO GENERATION EVENTS
// ============================================================================

/**
 * Events related to video generation
 */
export const VIDEO_EVENTS = {
  /** Fired when video generation starts */
  GENERATION_STARTED: 'video:generation-started',
  /** Fired during generation with progress updates */
  GENERATION_PROGRESS: 'video:generation-progress',
  /** Fired when video generation completes */
  GENERATION_COMPLETED: 'video:generation-completed',
  /** Fired when video generation fails */
  GENERATION_FAILED: 'video:generation-failed',
  /** Fired when video polling starts */
  POLLING_STARTED: 'video:polling-started',
  /** Fired during polling with status updates */
  POLLING_UPDATE: 'video:polling-update',
  /** Fired when video polling stops */
  POLLING_STOPPED: 'video:polling-stopped'
};

// ============================================================================
// AUTH EVENTS
// ============================================================================

/**
 * Events related to authentication and credits
 */
export const AUTH_EVENTS = {
  /** Fired when user logs in */
  LOGIN: 'auth:login',
  /** Fired when user logs out */
  LOGOUT: 'auth:logout',
  /** Fired when user data is updated */
  USER_UPDATED: 'auth:user-updated',
  /** Fired when credit balance changes */
  CREDITS_UPDATED: 'auth:credits-updated',
  /** Fired when operation fails due to insufficient credits */
  CREDITS_INSUFFICIENT: 'auth:credits-insufficient',
  /** Fired when auth session expires */
  SESSION_EXPIRED: 'auth:session-expired'
};

// ============================================================================
// UI EVENTS
// ============================================================================

/**
 * Events related to UI components
 */
export const UI_EVENTS = {
  /** Fired when a modal is opened */
  MODAL_OPENED: 'ui:modal-opened',
  /** Fired when a modal is closed */
  MODAL_CLOSED: 'ui:modal-closed',
  /** Fired when a toast notification is shown */
  TOAST_SHOWN: 'ui:toast-shown',
  /** Fired when idea canvas is opened */
  CANVAS_OPENED: 'ui:canvas-opened',
  /** Fired when idea canvas is closed */
  CANVAS_CLOSED: 'ui:canvas-closed',
  /** Fired when sidebar is toggled */
  SIDEBAR_TOGGLED: 'ui:sidebar-toggled',
  /** Fired when theme changes */
  THEME_CHANGED: 'ui:theme-changed',
  /** Fired when an image is selected via drop zone */
  IMAGE_SELECTED: 'ui:image-selected',
  /** Fired when an image is removed from drop zone */
  IMAGE_REMOVED: 'ui:image-removed'
};

// ============================================================================
// NAVIGATION EVENTS
// ============================================================================

/**
 * Events related to page navigation
 */
export const NAV_EVENTS = {
  /** Fired when a page finishes loading */
  PAGE_LOADED: 'nav:page-loaded',
  /** Fired when route changes (SPA navigation) */
  ROUTE_CHANGED: 'nav:route-changed'
};

// ============================================================================
// STORYBOARD EVENTS
// ============================================================================

/**
 * Events related to video storyboards
 */
export const STORYBOARD_EVENTS = {
  /** Fired when storyboard is loaded */
  LOADED: 'storyboard:loaded',
  /** Fired when a scene is updated */
  SCENE_UPDATED: 'storyboard:scene-updated',
  /** Fired when a scene video completes */
  SCENE_VIDEO_COMPLETE: 'storyboard:scene-video-complete',
  /** Fired when a panel is added to pool */
  PANEL_ADDED: 'storyboard:panel-added'
};

// ============================================================================
// ALL EVENTS (for iteration/debugging)
// ============================================================================

/**
 * Combined object of all events for iteration
 */
export const ALL_EVENTS = {
  ...IDEA_EVENTS,
  ...SESSION_EVENTS,
  ...GENERATOR_EVENTS,
  ...IMAGE_EVENTS,
  ...VIDEO_EVENTS,
  ...AUTH_EVENTS,
  ...UI_EVENTS,
  ...NAV_EVENTS,
  ...STORYBOARD_EVENTS
};

// ============================================================================
// TYPE HELPERS
// ============================================================================

/**
 * Get all event names as an array
 * @returns {string[]}
 */
export function getAllEventNames() {
  return Object.values(ALL_EVENTS);
}

/**
 * Check if a string is a valid event name
 * @param {string} name - Event name to check
 * @returns {boolean}
 */
export function isValidEventName(name) {
  return getAllEventNames().includes(name);
}

/**
 * Get event domain from event name
 * @param {string} eventName - Full event name (e.g., 'idea:created')
 * @returns {string} Domain portion (e.g., 'idea')
 */
export function getEventDomain(eventName) {
  return eventName.split(':')[0];
}

/**
 * Get event action from event name
 * @param {string} eventName - Full event name (e.g., 'idea:created')
 * @returns {string} Action portion (e.g., 'created')
 */
export function getEventAction(eventName) {
  return eventName.split(':')[1];
}
